import { selectDevice, selectSelectedAppType, selectSelectedMarket } from "../../state/selectors";
import { getState } from "../../state/store";

import {
  advancedDiagnosticCodes,
  chartNames,
  deviceGroups,
  eventStatuses,
  strCode
} from "../../consts";
import { t } from "../../services";
import {
  arrayUtil,
  deviceUtil,
  locationUtil,
  objectUtil,
  rumEventsUtil,
  stringUtil
} from "../../utils";

// Diagnostics Statistics
// _________________________________________________________________________
const getDiagnosticsStatistics = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({ chartName: chartNames.AD_STATISTICS });

  const deviceTypes = devices && devices[0] && deviceUtil.getDeviceTypesByName(devices[0]);

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices: deviceTypes,
    groupFn: (event) => rumEventsUtil.formatDiagnosticsResult(event?.eventDetails)
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;
  const codes = Object.keys(advancedDiagnosticCodes);

  const result = [];
  let totalADCount = 0;

  const hasGroups = {
    monoDevices: false,
    chargers: false,
    holders: false
  };

  grouped?.size > 0 &&
    codes.forEach((code) => {
      const events = grouped.get(code);

      const groupCount = {
        monoDevices: 0,
        chargers: 0,
        holders: 0
      };

      if (events && events?.length > 0) {
        events.forEach((event) => {
          const device = event?.eventDetails?.deviceTypeName;

          const group = deviceUtil.getDeviceGroup(device);

          if (group) {
            groupCount[group] = groupCount[group] + 1;
            hasGroups[group] = true;

            totalADCount += 1;
          }
        });
      }

      result.push({
        code: stringUtil.capitalize(code),
        metrics: {
          [metricsConfig.mono.id]: groupCount.monoDevices,
          [metricsConfig.charger.id]: groupCount.chargers,
          [metricsConfig.holder.id]: groupCount.holders
        }
      });
    });

  return {
    result: rumEventsUtil.filterResults({
      result,
      agregatorName: "code",
      devices,
      hasGroups,
      metricsConfig
    }),
    totalADCount
  };
};

const getDiagnosticCountByMarket = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.AD_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.market
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  let result = [];

  grouped.size > 0 &&
    grouped.forEach((events, market) => {
      const { workingProperlyAD, defectiveAD, failedAD, unknownAD, undefinedAD } =
        rumEventsUtil.getAdvancedDiagnosticsStatistics(events);

      result.push({
        market,
        metrics: {
          [metricsConfig.workingProperly.id]: workingProperlyAD,
          [metricsConfig.defective.id]: defectiveAD,
          [metricsConfig.unknown.id]: unknownAD,
          [metricsConfig.undefined.id]: undefinedAD,
          [metricsConfig.failed.id]: failedAD
        }
      });
    });

  return result;
};

const getDiagnosticCountByAppType = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.AD_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.appType
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  const result = [];

  grouped.size > 0 &&
    grouped.forEach((events, market) => {
      const { workingProperlyAD, defectiveAD, failedAD, unknownAD, undefinedAD } =
        rumEventsUtil.getAdvancedDiagnosticsStatistics(events);

      result.push({
        market,
        metrics: {
          [metricsConfig.workingProperly.id]: workingProperlyAD,
          [metricsConfig.defective.id]: defectiveAD,
          [metricsConfig.unknown.id]: unknownAD,
          [metricsConfig.undefined.id]: undefinedAD,
          [metricsConfig.failed.id]: failedAD
        }
      });
    });

  return result;
};

const getDiagnosticCountByLocation = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.AD_COUNT
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    groupFn: (event) => event.metadata?.store
  });

  if (!chartData?.grouped || !metricsConfig) {
    return [];
  }

  const { grouped } = chartData;

  let result = [];

  grouped.size > 0 &&
    grouped.forEach((events, location) => {
      const locationName = location
        ? locationUtil.getLocationNameById(location)
        : t(strCode.NO_LOCATION);

      if (locationName) {
        const { workingProperlyAD, defectiveAD, failedAD, unknownAD, undefinedAD } =
          rumEventsUtil.getAdvancedDiagnosticsStatistics(events);

        result.push({
          location: locationName,
          metrics: {
            [metricsConfig.workingProperly.id]: workingProperlyAD,
            [metricsConfig.defective.id]: defectiveAD,
            [metricsConfig.unknown.id]: unknownAD,
            [metricsConfig.undefined.id]: undefinedAD,
            [metricsConfig.failed.id]: failedAD
          }
        });
      }
    });

  return result;
};

// Diagnostics Statistics Reports
// _________________________________________________________________________
const getDiagnosticCountReport = (data, devices) => {
  const market = selectSelectedMarket()(getState());
  const appType = selectSelectedAppType()(getState());

  const isStatisticsByAppType = appType.length !== 1;
  const isStatisticsByMarket = !market || market.length !== 1;
  const isStatisticsByLocation = !isStatisticsByAppType && !isStatisticsByMarket;

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices,
    isReportConfig: true
  });

  if (!chartData?.grouped) {
    return [];
  }

  const { grouped, dates } = chartData;

  let result = [];

  let workingProperlyADTotal = 0;
  let defectiveADTotal = 0;
  let failedADTotal = 0;
  let unknownADTotal = 0;
  let undefinedADTotal = 0;

  grouped.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      if (events?.length) {
        const groupFn = isStatisticsByAppType
          ? (event) => event.appType
          : isStatisticsByMarket
          ? (event) => event.metadata?.market
          : (event) => event.metadata?.store;

        const groupedByParam = objectUtil.groupBy(events, groupFn);

        groupedByParam?.size > 0 &&
          groupedByParam.forEach((events, param) => {
            const locationName =
              isStatisticsByLocation && param
                ? locationUtil.getLocationNameById(param)
                : t(strCode.NO_LOCATION);

            if (param || isStatisticsByLocation) {
              const { workingProperlyAD, defectiveAD, failedAD, unknownAD, undefinedAD } =
                rumEventsUtil.getAdvancedDiagnosticsStatistics(events);

              workingProperlyADTotal += workingProperlyAD;
              defectiveADTotal += defectiveAD;
              failedADTotal += failedAD;
              unknownADTotal += unknownAD;
              undefinedADTotal += undefinedAD;

              let stats = {};

              if (isStatisticsByAppType) {
                stats["App"] = param;
              } else if (isStatisticsByMarket) {
                stats["Market"] = param;
              } else {
                stats["Location"] = locationName;
              }

              stats = Object.assign(stats, {
                ["Date"]: day,
                ["Working properly"]: workingProperlyAD,
                ["Defective"]: defectiveAD,
                ["Unknown"]: unknownAD,
                ["Undefined"]: undefinedAD,
                ["Failed"]: failedAD
              });

              result.push(stats);
            }
          });
      }
    });

  const totals = {
    ["Working properly"]: workingProperlyADTotal,
    ["Defective"]: defectiveADTotal,
    ["Unknown"]: unknownADTotal,
    ["Undefined"]: undefinedADTotal,
    ["Failed"]: failedADTotal
  };

  result.push(totals);

  return result;
};

// Diagnostics Performance
// _________________________________________________________________________
const getDiagnosticsPerformance = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.DIAGNOSTICS_PERFORMANCE
  });

  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let diagnostics = 0;
      let avgSpeed = 0;
      let chargersAvgSpeed = 0;
      let holdersAvgSpeed = 0;

      const successCases = [
        advancedDiagnosticCodes.defective,
        advancedDiagnosticCodes.Unknown,
        advancedDiagnosticCodes.Failed,
        "working_properly"
      ];

      if (events && events?.length > 0) {
        const chargerDurationsList = [];
        const holderDurationsList = [];
        events.forEach((event) => {
          if (
            event?.eventDetails?.duration !== null &&
            successCases.includes(event?.eventDetails?.result?.device_state)
          ) {
            if (deviceGroups.chargers.includes(event.eventDetails.deviceTypeName)) {
              chargerDurationsList.push(event.eventDetails.duration);
            } else {
              holderDurationsList.push(event.eventDetails.duration);
            }
            ++diagnostics;
          }
        });

        const selectedDeviceFilter = selectDevice()(getState());
        const group = deviceUtil.getDeviceGroupByName(selectedDeviceFilter.value);

        if (group === "duoDevices") {
          chargersAvgSpeed = chargerDurationsList.length
            ? arrayUtil.getAverageValue(chargerDurationsList)
            : 0;

          holdersAvgSpeed = holderDurationsList.length
            ? arrayUtil.getAverageValue(holderDurationsList)
            : 0;

          avgSpeed = chargersAvgSpeed + holdersAvgSpeed;
        } else {
          avgSpeed =
            holderDurationsList.length || chargerDurationsList.length
              ? arrayUtil.getAverageValue([...holderDurationsList, ...chargerDurationsList])
              : 0;
        }
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.diagnostics.id]: diagnostics,
          [metricsConfig.speed.id]: avgSpeed
        }
      });
    });

  return result;
};

const getDiagnosticsErrorsReadingPerfomance = (data, devices) => {
  const metricsConfig = rumEventsUtil.getMetricsConfig({
    chartName: chartNames.DIAGNOSTICS_ERRORS_READING_PERFOMANCE
  });
  const chartData = rumEventsUtil.prepareChartData({
    data,
    devices
  });

  if (!chartData?.grouped || !chartData?.dates || !metricsConfig) {
    return [];
  }

  const { grouped, dates } = chartData;

  const result = [];

  grouped?.size > 0 &&
    dates.forEach((day) => {
      const events = grouped.get(day);

      let avgRecords = 0;
      let chargerAvgRecords = 0;
      let holderAvgRecords = 0;

      let avgSpeed = 0;
      let chargersAvgSpeed = 0;
      let holdersAvgSpeed = 0;

      if (events && events?.length > 0) {
        const chargerRecordsList = [];
        const holderRecordsList = [];

        const chargerDurationsList = [];
        const holderDurationsList = [];

        events.forEach((event) => {
          if (
            event?.eventDetails?.result?.framesCount !== undefined &&
            event?.eventDetails?.status === eventStatuses.SUCCESS
          ) {
            if (deviceGroups.chargers.includes(event.eventDetails.deviceTypeName)) {
              chargerRecordsList.push(event.eventDetails.result.framesCount);
            } else {
              holderRecordsList.push(event.eventDetails.result?.framesCount);
            }
          }

          if (event?.eventDetails?.duration !== undefined) {
            if (deviceGroups.chargers.includes(event.eventDetails.deviceTypeName)) {
              chargerDurationsList.push(event.eventDetails.duration);
            } else {
              holderDurationsList.push(event.eventDetails.duration);
            }
          }
        });

        const selectedDeviceFilter = selectDevice()(getState());
        const group = deviceUtil.getDeviceGroupByName(selectedDeviceFilter.value);

        if (group === "duoDevices") {
          chargerAvgRecords = chargerRecordsList.length
            ? arrayUtil.getAverageValue(chargerRecordsList)
            : 0;

          holderAvgRecords = holderRecordsList.length
            ? arrayUtil.getAverageValue(holderRecordsList)
            : 0;

          chargersAvgSpeed = chargerDurationsList.length
            ? arrayUtil.getAverageValue(chargerDurationsList)
            : 0;

          holdersAvgSpeed = holderDurationsList.length
            ? arrayUtil.getAverageValue(holderDurationsList)
            : 0;

          avgRecords = chargerAvgRecords + holderAvgRecords;

          avgSpeed = chargersAvgSpeed + holdersAvgSpeed;
        } else {
          avgRecords =
            holderRecordsList.length || chargerRecordsList.length
              ? arrayUtil.getAverageValue([...holderRecordsList, ...chargerRecordsList])
              : 0;

          avgSpeed =
            holderDurationsList.length || chargerDurationsList.length
              ? arrayUtil.getAverageValue([...holderDurationsList, ...chargerDurationsList])
              : 0;
        }
      }

      result.push({
        date: day,
        metrics: {
          [metricsConfig.diagnostics.id]: Math.floor(avgRecords),
          [metricsConfig.speed.id]: parseFloat(avgSpeed.toFixed(2))
        }
      });
    });

  return result;
};

const diagnosticDashboardUtil = {
  // Diagnostics Statistics
  // _________________________________________________________________________
  getDiagnosticsStatistics,
  getDiagnosticCountByMarket,
  getDiagnosticCountByAppType,
  getDiagnosticCountByLocation,

  // Diagnostics Statistics Reports
  // _________________________________________________________________________
  getDiagnosticCountReport,

  // Diagnostics Performance
  // _________________________________________________________________________
  getDiagnosticsPerformance,
  // Diagnostic Errors
  //
  getDiagnosticsErrorsReadingPerfomance
};

export default diagnosticDashboardUtil;
